import React, { useState, useEffect, useCallback, useMemo } from 'react';

// Revenue line options grouped by business line feature flag
const REVENUE_LINE_OPTIONS = {
  wealth: [
    { value: 'wealth_fee_based',     label: 'Fee-Based' },
    { value: 'wealth_trailer',       label: 'Trailer' },
    { value: 'wealth_transactional', label: 'Transactional' },
    { value: 'wealth_other',         label: 'Other (Wealth)' },
  ],
  life: [
    { value: 'life_first_year', label: 'Life – First Year' },
    { value: 'life_renewal',    label: 'Life – Renewal' },
    { value: 'life_override',   label: 'Life – Override' },
    { value: 'life_other',      label: 'Life – Other' },
  ],
  pc: [
    { value: 'pc_personal',   label: 'P&C – Personal' },
    { value: 'pc_commercial', label: 'P&C – Commercial' },
  ],
  group: [
    { value: 'group_first_year', label: 'Group – First Year' },
    { value: 'group_renewal',    label: 'Group – Renewal' },
    { value: 'group_override',   label: 'Group – Override' },
    { value: 'group_other',      label: 'Group – Other' },
  ],
};

// Which feature flags enable each group
const FEATURE_MAP = {
  wealth: 'multi_line_valuation_wealth_management',
  life:   'multi_line_valuation_life_insurance',
  pc:     'multi_line_valuation_p_and_c',
  group:  'multi_line_valuation_group',
};

function buildRevenueOptions(features) {
  const opts = [{ value: '', label: '— Unassigned —' }];
  Object.entries(REVENUE_LINE_OPTIONS).forEach(([group, lines]) => {
    const required = FEATURE_MAP[group];
    if (!required || (features || []).includes(required)) {
      opts.push(...lines);
    }
  });
  opts.push({ value: 'exclude', label: 'Exclude' });
  return opts;
}

// --- Shared helpers ---

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

// Returns depth of a fully_qualified_name based on colon separators.
function accountDepth(fqn) {
  return (fqn || '').split(':').length - 1;
}

function leafName(fqn) {
  const parts = (fqn || '').split(':');
  return parts[parts.length - 1].trim();
}

// Returns true if any ancestor of fullyQualifiedName is in mappedParentNames.
function hasParentMapped(fqn, mappedParentNames) {
  const parts = (fqn || '').split(':');
  for (let i = 1; i < parts.length; i++) {
    if (mappedParentNames.has(parts.slice(0, i).join(':'))) return true;
  }
  return false;
}

// --- AccountMappingTable: shared table for both revenue and expense tabs ---

function AccountMappingTable({ accounts, mappings, onChangeMapping, options, disabledText, parentMappedCheck }) {
  if (!accounts || accounts.length === 0) {
    return (
      <div className="mt-4 text-sm text-succession-text-secondary">
        No accounts found in QuickBooks.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-succession-border-primary">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-succession-bg-secondary border-b border-succession-border-primary">
            <th className="text-left px-3 py-2 text-xs font-medium text-succession-text-secondary">
              Account
            </th>
            <th className="text-left px-3 py-2 text-xs font-medium text-succession-text-secondary">
              Maps To
            </th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((acct, idx) => {
            const fqn = acct.fully_qualified_name || acct.name;
            const depth = accountDepth(fqn);
            const name = leafName(fqn);
            const parentMapped = parentMappedCheck(fqn);

            return (
              <tr
                key={acct.id}
                className={
                  parentMapped
                    ? 'bg-succession-bg-secondary opacity-50'
                    : idx % 2 === 0
                      ? 'bg-succession-bg-card'
                      : 'bg-succession-bg-secondary'
                }
              >
                <td
                  className="py-2 pr-3 text-succession-text-primary"
                  style={{ paddingLeft: `${0.75 + depth * 1.25}rem` }}
                >
                  {depth > 0 && (
                    <span className="mr-1 text-succession-text-tertiary select-none">↳</span>
                  )}
                  <span className={depth > 0 ? 'text-succession-text-secondary' : ''}>
                    {name}
                  </span>
                </td>
                <td className="px-3 py-2">
                  {parentMapped ? (
                    <span className="text-xs text-succession-text-tertiary italic">
                      {disabledText}
                    </span>
                  ) : (
                    <select
                      value={mappings[acct.id] || ''}
                      onChange={(e) => onChangeMapping(acct.id, e.target.value)}
                      className="w-full text-sm rounded-md border border-succession-border-primary bg-succession-bg-card text-succession-text-primary px-2 py-1 focus:outline-none focus:ring-2 focus:ring-succession-accent"
                    >
                      {options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// --- RevenueTab ---

function RevenueTab({ quickbooks_accounts_url, quickbooks_account_mappings_url, features }) {
  const [accounts, setAccounts] = useState(null);
  const [mappings, setMappings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [acctRes, mapRes] = await Promise.all([
        fetch(quickbooks_accounts_url, {
          headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
        }),
        fetch(quickbooks_account_mappings_url, {
          headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
        }),
      ]);

      const acctData = await acctRes.json();
      const mapData = await mapRes.json();

      if (!acctRes.ok) throw new Error(acctData.error || 'Failed to load accounts');

      setAccounts(acctData.accounts || []);

      const mappingIndex = {};
      (mapData.mappings || []).forEach((m) => {
        mappingIndex[m.qbo_account_id] = m.revenue_line;
      });
      setMappings(mappingIndex);
    } catch (e) {
      setError(e.message || 'Failed to load account data.');
    } finally {
      setLoading(false);
    }
  }, [quickbooks_accounts_url, quickbooks_account_mappings_url]);

  useEffect(() => { loadData(); }, [loadData]);

  const mappedParentNames = useMemo(() => {
    const result = new Set();
    (accounts || []).forEach((acct) => {
      const val = mappings[acct.id];
      if (val && val !== 'exclude') {
        result.add(acct.fully_qualified_name || acct.name);
      }
    });
    return result;
  }, [accounts, mappings]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    setError(null);

    const payload = (accounts || [])
      .filter((acct) => !hasParentMapped(acct.fully_qualified_name || acct.name, mappedParentNames))
      .map((acct) => ({
        qbo_account_id:   acct.id,
        qbo_account_name: acct.fully_qualified_name || acct.name,
        revenue_line:     mappings[acct.id] || '',
      }))
      .filter((m) => m.revenue_line !== '');

    try {
      const response = await fetch(quickbooks_account_mappings_url, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken(),
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ mappings: payload }),
      });
      const data = await response.json();
      if (response.ok) {
        setMessage('Mappings saved. Run "Sync Latest Data" to apply them.');
      } else {
        setError(data.error || 'Failed to save mappings.');
      }
    } catch {
      setError('Failed to save mappings.');
    } finally {
      setSaving(false);
    }
  };

  const options = buildRevenueOptions(features);

  if (loading) {
    return <div className="mt-4 text-sm text-succession-text-secondary">Loading accounts…</div>;
  }

  if (error && !accounts) {
    return <div className="mt-4 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>;
  }

  return (
    <div>
      <p className="text-xs text-succession-text-secondary mb-3">
        Assign each income account to a revenue line. Sub-accounts are indented under their
        parent — mapping a parent automatically excludes its sub-accounts since their values
        are already included in the parent total. Unmapped accounts still count toward total revenue.
      </p>

      <AccountMappingTable
        accounts={accounts}
        mappings={mappings}
        onChangeMapping={(id, val) => setMappings((prev) => ({ ...prev, [id]: val }))}
        options={options}
        disabledText="Excluded — parent account mapped"
        parentMappedCheck={(fqn) => hasParentMapped(fqn, mappedParentNames)}
      />

      {message && (
        <p className="mt-3 text-sm text-green-700 bg-green-50 rounded-lg px-3 py-2">{message}</p>
      )}
      {error && (
        <p className="mt-3 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>
      )}

      <button
        onClick={handleSave}
        disabled={saving}
        className="mt-4 px-4 py-2 text-sm font-medium rounded-lg bg-succession-accent text-white hover:bg-succession-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {saving ? 'Saving…' : 'Save Mappings'}
      </button>
    </div>
  );
}

// --- ExpenseTab ---

function ExpenseTab({ quickbooks_expense_accounts_url, quickbooks_expense_mappings_url, expenseCategories }) {
  const [accounts, setAccounts] = useState(null);
  const [mappings, setMappings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const categoryOptions = useMemo(() => {
    const opts = [{ value: '', label: '— Unassigned —' }];
    expenseCategories.forEach((cat) => opts.push({ value: cat.key, label: cat.label }));
    return opts;
  }, [expenseCategories]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [acctRes, mapRes] = await Promise.all([
        fetch(quickbooks_expense_accounts_url, {
          headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
        }),
        fetch(quickbooks_expense_mappings_url, {
          headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
        }),
      ]);

      const acctData = await acctRes.json();
      const mapData = await mapRes.json();

      if (!acctRes.ok) throw new Error(acctData.error || 'Failed to load expense accounts');

      setAccounts(acctData.accounts || []);

      const mappingIndex = {};
      (mapData.mappings || []).forEach((m) => {
        mappingIndex[m.qbo_account_id] = m.expense_category_key;
      });
      setMappings(mappingIndex);
    } catch (e) {
      setError(e.message || 'Failed to load expense account data.');
    } finally {
      setLoading(false);
    }
  }, [quickbooks_expense_accounts_url, quickbooks_expense_mappings_url]);

  useEffect(() => { loadData(); }, [loadData]);

  const mappedParentNames = useMemo(() => {
    const result = new Set();
    (accounts || []).forEach((acct) => {
      const val = mappings[acct.id];
      if (val) result.add(acct.fully_qualified_name || acct.name);
    });
    return result;
  }, [accounts, mappings]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    setError(null);

    const payload = (accounts || [])
      .filter((acct) => !hasParentMapped(acct.fully_qualified_name || acct.name, mappedParentNames))
      .map((acct) => ({
        qbo_account_id:       acct.id,
        qbo_account_name:     acct.fully_qualified_name || acct.name,
        expense_category_key: mappings[acct.id] || '',
      }))
      .filter((m) => m.expense_category_key !== '');

    try {
      const response = await fetch(quickbooks_expense_mappings_url, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken(),
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ mappings: payload }),
      });
      const data = await response.json();
      if (response.ok) {
        setMessage('Expense mappings saved. Run "Sync Latest Data" to apply them.');
      } else {
        setError(data.error || 'Failed to save expense mappings.');
      }
    } catch {
      setError('Failed to save expense mappings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="mt-4 text-sm text-succession-text-secondary">Loading expense accounts…</div>;
  }

  if (error && !accounts) {
    return <div className="mt-4 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>;
  }

  if (expenseCategories.length === 0) {
    return (
      <div className="mt-4 text-sm text-succession-text-secondary">
        No expense categories configured for your company. Contact your administrator to set up expense categories before mapping.
      </div>
    );
  }

  return (
    <div>
      <p className="text-xs text-succession-text-secondary mb-3">
        Assign each QBO expense account to one of your company's expense categories. Mapped values
        will pre-populate the Expenses tab in your valuation when you sync. Sub-accounts are
        indented — mapping a parent automatically covers its sub-accounts.
      </p>

      <AccountMappingTable
        accounts={accounts}
        mappings={mappings}
        onChangeMapping={(id, val) => setMappings((prev) => ({ ...prev, [id]: val }))}
        options={categoryOptions}
        disabledText="Excluded — parent account mapped"
        parentMappedCheck={(fqn) => hasParentMapped(fqn, mappedParentNames)}
      />

      {message && (
        <p className="mt-3 text-sm text-green-700 bg-green-50 rounded-lg px-3 py-2">{message}</p>
      )}
      {error && (
        <p className="mt-3 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>
      )}

      <button
        onClick={handleSave}
        disabled={saving}
        className="mt-4 px-4 py-2 text-sm font-medium rounded-lg bg-succession-accent text-white hover:bg-succession-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {saving ? 'Saving…' : 'Save Expense Mappings'}
      </button>
    </div>
  );
}

// --- QuickbooksAccountMapping (tabbed container) ---

const QuickbooksAccountMapping = ({
  quickbooks_accounts_url,
  quickbooks_account_mappings_url,
  quickbooks_expense_accounts_url,
  quickbooks_expense_mappings_url,
  quickbooks_expense_categories,
  features,
}) => {
  const [activeTab, setActiveTab] = useState('revenue');
  const showExpenseTab = !!quickbooks_expense_accounts_url && !!quickbooks_expense_mappings_url;

  return (
    <div className="mt-6">
      <h3 className="text-sm font-semibold text-succession-text-primary mb-3">
        Chart of Accounts Mapping
      </h3>

      {showExpenseTab && (
        <div className="flex border-b border-succession-border-primary mb-4">
          {['revenue', 'expenses'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={[
                'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === tab
                  ? 'border-succession-accent text-succession-accent'
                  : 'border-transparent text-succession-text-secondary hover:text-succession-text-primary',
              ].join(' ')}
            >
              {tab === 'revenue' ? 'Revenue' : 'Expenses'}
            </button>
          ))}
        </div>
      )}

      {activeTab === 'revenue' && (
        <RevenueTab
          quickbooks_accounts_url={quickbooks_accounts_url}
          quickbooks_account_mappings_url={quickbooks_account_mappings_url}
          features={features}
        />
      )}

      {activeTab === 'expenses' && showExpenseTab && (
        <ExpenseTab
          quickbooks_expense_accounts_url={quickbooks_expense_accounts_url}
          quickbooks_expense_mappings_url={quickbooks_expense_mappings_url}
          expenseCategories={quickbooks_expense_categories || []}
        />
      )}
    </div>
  );
};

export default QuickbooksAccountMapping;
